import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import RestClient from '../RestClient/RestClient';
import { apiHosts } from '../RestClient/FetchHelper.config';

const getVersionConfig = (config) => {
    const context = RestClient.getContext();
    /* istanbul ignore else */
    const isDesktop = context.deviceType && context.deviceType.isDesktop;
    /* istanbul ignore next */
    if (isDesktop === true) {
        config.version = '/v2';
        config.acceptVersion = '4';
    }
    return config;
};
export const transformPriceForBrowse = (
    priceData,
    enableCouponInfoPrice,
    enableEligibleCouponInfoPrice = true
) => {
    const { id, pricing } = priceData;
    const {
        amounts = [],
        minSavings,
        maxSavings,
        manufacturerAdvertised = false,
        marketingLabel,
        couponInfo,
        eligibleCouponInfo,
        promotions,
        priceLabel: eachLabel = '',
    } = pricing.root || pricing;
    const updatedAmounts = amounts.map((amount) => {
        if (amount.type === 'DEFAULT') {
            amount.minPercentOff = minSavings || 0;
            amount.maxPercentOff = maxSavings || 0;
        } else if (amount.type === 'ORIGINAL') {
            amount.minPercentOff = 0;
            amount.maxPercentOff = 0;
        }
        return amount;
    });
    const coupon = enableCouponInfoPrice ? get(couponInfo, [0]) : {};
    const eligibleCoupon = enableEligibleCouponInfoPrice ? get(eligibleCouponInfo, [0]) : {};
    const couponPrice = {};
    const eligibilityMessage = enableEligibleCouponInfoPrice
        ? get(eligibleCoupon, 'eligibilityMessage', '')
        : '';
    const couponObj = coupon || eligibleCoupon;

    const fpac = couponObj && {
        fpacPriceMax: get(couponObj, 'amount.max', ''),
        fpacPriceMin: get(couponObj, 'amount.min', ''),
        fpacCoupon: get(couponObj, 'alphaId', ''),
    };
    couponPrice.fpac = !isEmpty(couponObj) && fpac;

    return {
        id,
        amounts: updatedAmounts,
        manufacturerAdvertised,
        marketingLabel,
        promotions,
        eachLabel,
        eligibilityMessage,
        ...couponPrice,
    };
};

const getHostConfig = (config) => {
    const context = RestClient.getContext();
    /* istanbul ignore next */
    if (context && context.apiHosts && context.apiHosts[config.apiHost]) {
        config.host = context.apiHosts[config.apiHost];
    }
    return config;
};

const setPrice = (prices, price, enableCouponInfoPrice, enableEligibleCouponInfoPrice = true) => {
    /* istanbul ignore else */
    if (price && price.id) {
        prices.push(
            transformPriceForBrowse(price, enableCouponInfoPrice, enableEligibleCouponInfoPrice)
        );
    }
};

const getProductsPriceByPPIDs = (params, config) => {
    const promise = new Promise((resolve, reject) => {
        /* istanbul ignore next */
        const { productIds } = params;
        /* istanbul ignore next */
        if (!productIds && productIds.length && !productIds.length > 0) {
            reject({
                err: 'Invalid product is. Expected array of product Ids',
            });
        }

        config.params.ppId = productIds.join(',');

        RestClient.get(config).then((json) => {
            const prices = [];
            let allProducts = [];
            let fetchStatus = true;
            /* istanbul ignore else */
            if (json.data && json.data.length > 0) {
                allProducts = json.data;
                allProducts.forEach((element) => {
                    setPrice(
                        prices,
                        element,
                        params.enableCouponInfoPrice,
                        params.enableEligibleCouponInfoPrice
                    );
                });
            } else {
                fetchStatus = false;
            }
            resolve({ prices, fetchStatus, allProducts });
        });
    });

    return promise;
};

const getProductPrice = (ppId, config) => {
    /* istanbul ignore else */
    /* istanbul ignore next */
    config.params.ppId = ppId;

    return RestClient.get(config).then((json) => json.data);
};

const getProductPriceByEachPPID = (params, config) => {
    const promise = new Promise((resolve, reject) => {
        /* istanbul ignore next */
        const { productIds } = params;
        /* istanbul ignore next */
        if (!productIds && productIds.length && !productIds.length > 0) {
            reject({
                err: 'Invalid product is. Expected array of product Ids',
            });
        }

        const ppIds = productIds.map((ppId) => getProductPrice(ppId, config));

        return Promise.all(ppIds).then((products) => {
            const prices = [];
            const allProducts = [];
            let fetchStatus = true;
            /* istanbul ignore else */
            if (products && products.length > 0) {
                products.forEach((element) => {
                    /* istanbul ignore else */
                    if (element && element.length > 0) {
                        setPrice(
                            prices,
                            element[0],
                            params.enableCouponInfoPrice,
                            params.enableEligibleCouponInfoPrice
                        );
                        allProducts.push(element[0]);
                    }
                });
            } else {
                /* istanbul ignore next */
                fetchStatus = false;
            }
            resolve({ prices, fetchStatus, allProducts });
        });
    });

    return promise;
};

export const getProductsPriceByBrowseApi = (params) => {
    const queryParams = {};
    /* istanbul ignore next */
    if (params.regionZone) {
        queryParams.regionId = params.regionZone;
    }
    let config;
    /* istanbul ignore else */
    if (params.apiUrl) {
        config = {
            url: params.apiUrl,
            external: true,
            params: queryParams,
        };
    } else {
        const pricingUrl = 'product-summaries-aggregator';
        config = {
            url: pricingUrl,
            external: false,
            params: queryParams,
            version: '/browse-aggregator/v1',
            apiHost: apiHosts.browse,
        };
    }

    if (params.cachedAPI) {
        config.url = 'cached-product-summaries-aggregator';
    }

    config = getHostConfig(config);

    if (params.doIndividualPP) {
        return getProductPriceByEachPPID(params, config);
    }
    return getProductsPriceByPPIDs(params, config);
};

export const getPriceByAllProducts = (params) => {
    const promise = new Promise((resolve, reject) => {
        /* istanbul ignore next */
        const { productIds = [] } = params;
        /* istanbul ignore next */
        if (!productIds.length > 0) {
            reject({
                err: 'Invalid product is. Expected array of product Ids',
            });
        }

        const pricingUrl = 'product-summaries-aggregator';
        const queryParams = {
            ppId: productIds.join(','),
        };

        /* istanbul ignore else */
        /* istanbul ignore next */
        if (params.regionZone) {
            queryParams.regionId = params.regionZone;
        }

        let config = {
            url: pricingUrl,
            external: false,
            params: queryParams,
            version: '/browse-aggregator/v2',
            apiHost: apiHosts.browse,
        };
        config = getHostConfig(config);

        RestClient.get(config).then((json) => {
            const prices = [];
            /* istanbul ignore else */
            if (json && json.data) {
                const products = json.data.response ? json.data.response : json.data;

                products.forEach((element) => {
                    /* istanbul ignore else */
                    if (element) {
                        prices.push(transformPriceForBrowse(element));
                    }
                });
            }
            resolve(prices);
        });
    });

    return promise;
};

/**
 * Function to fetch product pricing through Search API.
 */
export const getPriceByProductSearch = (params = []) => {
    let productIds = null;

    /* istanbul ignore next */
    if (Array.isArray(params)) {
        productIds = params.map(({ id }) => id);
    } else {
        productIds = params.id ? [params.id] : [];
    }

    return getPriceByAllProducts({
        productIds,
        ...params,
    }).then((res) => res);
};

/**
 * Function to fetch all product pricing.
 */
export const getPriceByProduct = (params) => {
    /* istanbul ignore next */
    if (!params.callRootPriceAPI) {
        return getPriceByProductSearch(params);
    }
    const pricingUrl = `products/${params.id}/pricing/root`;
    const queryParams = {};
    /* istanbul ignore else */
    if (params.regionZone) {
        queryParams.regionId = params.regionZone;
    }
    let config = {
        url: pricingUrl,
        external: false,
        credentials: 'same-origin',
        params: queryParams,
    };
    config = getVersionConfig(config);
    config.enforceHTTP = true;
    return RestClient.get(config).then((json) => {
        let price = {};
        /* istanbul ignore else */
        if (json.data) {
            price = {
                id: params.id,
                amounts: json.data.amounts,
                manufacturerAdvertised: json.data.manufacturerAdvertised || false,
                marketingLabel: json.data.marketingLabel || null,
            };
        }
        return price;
    });
};
